import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "components/basic/confirmDialog";
import Scrollbar from "components/basic/scrollbar";

import CommentBubble from "./components/CommentBubble";
import { TicketComment } from "../../../../types";

interface CommentListProps {
  comments: TicketComment[];
  asAdmin: boolean;
  onDelete?: (commentId: number) => Promise<void>;
}

const CommentList: React.FC<CommentListProps> = ({
  comments,
  asAdmin,
  onDelete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("support");
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const lastId = comments.length ? comments[comments.length - 1].id : null;
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lastId]);

  const confirmDelete = async () => {
    if (pendingDelete == null || !onDelete) {
      return;
    }
    setDeleting(true);
    await onDelete(pendingDelete);
    setDeleting(false);
    setPendingDelete(null);
  };

  return (
    <Scrollbar scrollRef={containerRef} sx={{ flex: 1, minHeight: 0, py: 1 }}>
      {comments.map((comment) => (
        <CommentBubble
          key={comment.id}
          comment={comment}
          // Admins write as the support team, requesters as themselves.
          isMine={comment.fromAdmin === asAdmin}
          onDelete={onDelete ? () => setPendingDelete(comment.id) : undefined}
        />
      ))}

      {pendingDelete != null && (
        <ConfirmDialog
          title={t("detail.delete_comment_title")}
          body={t("detail.delete_comment_body")}
          confirmLabel={t("detail.delete_confirm")}
          destructive
          loading={deleting}
          onConfirm={confirmDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </Scrollbar>
  );
};

export default CommentList;
